/**
 * proposalSend — PROPOSAL-FLOW-02 "Build → Review → Decide" (Seth 2026-10-16).
 *
 * Everything that happens when a proposal LEAVES the building lives here, so
 * the office builder, the homeowner page's staff bar and the phone wizard all
 * send the same way and stamp the same facts:
 *
 *   • who it can go to   — useProposalRecipients: named contacts first, then
 *                          the ESC on-file lines/addresses (read-only mirror)
 *   • how it goes        — text through the office Podium line (podium-send),
 *                          email through send-email; both return
 *                          {blocked:"testing"} while comms are in testing
 *   • what gets recorded — pegasus_quote_mark_sent (status Sent on the first
 *                          send, the clock starts, sent_via/to/by, ONE specific
 *                          activity line: "Texted the link to (952) 555-1212")
 *   • the notes          — the note to the homeowner (shows on their page) and
 *                          the note to the office (never shown), saved at send
 *   • new people         — a typed mobile/email is saved to the customer's
 *                          Pegasus contacts (never ESC — DEC-22)
 *
 * Reviewing or presenting never changes the status: pegasus_quote_presented
 * stamps presented_at + "Presented the customer view · Miles S." only.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/* ── the office note convention (QUOTE-POLISH-11) ─────────────────────────
   quote.notes is OFFICE-ONLY and structured — the wizard writes a provenance
   line ("Built on site at call #N by Miles.") plus "Note to the office: …";
   editors touch only that tail so the stamp survives. ──────────────────── */
export const OFFICE_NOTE_RE = /(?:^|\n)Note to the office: ([\s\S]*)$/;
export function splitOfficeNote(notes: string | null | undefined): { head: string; note: string } {
  const raw = (notes ?? "").trim();
  const m = OFFICE_NOTE_RE.exec(raw);
  if (m) return { head: raw.slice(0, m.index).trim(), note: m[1].trim() };
  if (/^(Built|Presented) on site/.test(raw) && !raw.includes("\n")) return { head: raw, note: "" };
  return { head: "", note: raw };
}
export function joinOfficeNote(head: string, note: string): string | null {
  const parts = [head.trim() || null, note.trim() ? `Note to the office: ${note.trim()}` : null].filter(Boolean);
  return parts.length ? parts.join("\n") : null;
}

/* ── the link ──────────────────────────────────────────────────────────── */
export function proposalUrlFor(token: string): string {
  const base = typeof window !== "undefined" && /localhost|127\.0\.0\.1/.test(window.location.hostname)
    ? window.location.origin
    : "https://my.sedgwickheating.com";
  return `${base}/proposal?t=${encodeURIComponent(token)}`;
}

/* ── phones ────────────────────────────────────────────────────────────── */
export function p10(s: string | null | undefined): string {
  return (s ?? "").replace(/\D/g, "").slice(-10);
}
export function prettyPhone(s: string | null | undefined): string {
  const d = p10(s);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (s ?? "");
}
export const looksEmail = (s: string) => /^\S+@\S+\.\S+$/.test(s.trim());

/* ── the words ─────────────────────────────────────────────────────────── */
export const PROPOSAL_EMAIL_SUBJECT = "Your Sedgwick Heating proposal";
export function defaultTextMessage(firstName?: string | null): string {
  return `Hi${firstName ? ` ${firstName}` : ""}! Here's your Sedgwick Heating proposal — open it any time, no password needed. Reply here with any questions.`;
}
export function defaultEmailMessage(): string {
  return "Thanks for having us out. Your proposal is ready — open it any time, no password needed. Reply to this email or call (952) 881-9000 with any questions.";
}
/** The text that actually goes out: the message, then the link on its own line. */
export function textBodyWithLink(message: string, url: string): string {
  return `${message.trim()}\n${url}`.trim();
}
/** The branded proposal email (the mark, one paragraph from the advisor, the button). */
export function proposalEmailHtml(url: string, message?: string | null): string {
  const LOGO = "https://trfusfityhwecvglnfhm.supabase.co/storage/v1/object/public/email-assets/sedgwick-logo.png";
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const para = (message ?? "").trim() || defaultEmailMessage();
  return `<div style="max-width:480px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#fff;border-radius:14px;overflow:hidden;border:1px solid #e8eaed">
    <div style="text-align:center;padding:26px 24px 20px;border-bottom:3px solid #16314f"><img src="${LOGO}" alt="Sedgwick Heating" style="width:180px;max-width:60%"/></div>
    <div style="padding:30px 34px">
      <h1 style="font-size:20px;color:#16314f;margin:0 0 12px">Your comfort proposal</h1>
      <p style="color:#5f6b7a;font-size:15px;line-height:1.6;margin:0 0 22px;white-space:pre-line">${esc(para)}</p>
      <div style="text-align:center"><a href="${url}" style="display:inline-block;background:#c0392b;color:#fff;text-decoration:none;padding:13px 32px;border-radius:9px;font-weight:700;font-size:15px">View my proposal</a></div>
      <p style="color:#9aa4b1;font-size:13px;line-height:1.6;margin:24px 0 0">Questions? Call <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a>.</p>
    </div>
    <div style="text-align:center;font-size:12px;color:#9aa4b1;padding:0 0 22px">Sedgwick Heating &amp; Air Conditioning</div>
  </div>`;
}
export function proposalEmailText(url: string, message?: string | null): string {
  return `${(message ?? "").trim() || defaultEmailMessage()}\n\n${url}\n\nSedgwick Heating & Air Conditioning · (952) 881-9000`;
}

/* ── who it can go to ──────────────────────────────────────────────────── */
export interface Recipient {
  kind: "phone" | "email";
  /** phone: 10 digits · email: lower-cased */
  value: string;
  /** "Kevin Moore · owner" / "Phone 1 (on file)" */
  label: string;
  why: string;
  fromContact: boolean;
}
interface ContactLite { id: string; name: string; role: string | null; phone: string | null; email: string | null; gets_scheduling: boolean; locno: string | null }

/** Named Pegasus contacts first, then the ESC on-file lines and addresses
 *  those contacts don't already cover (mirror tables — read-only). */
export function useProposalRecipients(custno: string | null | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["proposal_recipients", custno ?? null, locno ?? null],
    enabled: !!custno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<{ phones: Recipient[]; emails: Recipient[] }> => {
      let cq = supabase.from("contact").select("id, name, role, phone, email, gets_scheduling, locno").eq("custno", custno!);
      cq = locno ? cq.or(`locno.is.null,locno.eq.${locno}`) : cq;
      const [{ data: contacts }, { data: cust }, { data: locs }] = await Promise.all([
        cq.order("created_at"),
        supabase.from("mirror_customer").select("phone1, phone2, phone3, phone4, lbl_phone1, lbl_phone2, lbl_phone3, lbl_phone4").eq("custno", custno!).maybeSingle(),
        supabase.from("mirror_location").select("locno, phone1, phone2, phone3, phone4, email, email2, email3").eq("custno", custno!),
      ]);
      const phones: Recipient[] = [];
      const emails: Recipient[] = [];
      const seenP = new Set<string>();
      const seenE = new Set<string>();
      const pushP = (raw: string | null | undefined, label: string, why: string, fromContact: boolean) => {
        const d = p10(raw);
        if (d.length !== 10 || seenP.has(d)) return;
        seenP.add(d);
        phones.push({ kind: "phone", value: d, label, why, fromContact });
      };
      const pushE = (raw: string | null | undefined, label: string, why: string, fromContact: boolean) => {
        const v = (raw ?? "").trim().toLowerCase();
        if (!v || !v.includes("@") || seenE.has(v)) return;
        seenE.add(v);
        emails.push({ kind: "email", value: v, label, why, fromContact });
      };
      for (const c of (contacts ?? []) as ContactLite[]) {
        const label = c.name + (c.role ? ` · ${c.role}` : "");
        const why = c.gets_scheduling ? "gets job updates" : "named contact";
        pushP(c.phone, label, why, true);
        pushE(c.email, label, why, true);
      }
      const c = (cust ?? {}) as Record<string, string | null>;
      pushP(c.phone1, c.lbl_phone1 || "Phone 1", "on file in ESC", false);
      pushP(c.phone2, c.lbl_phone2 || "Phone 2", "on file in ESC", false);
      pushP(c.phone3, c.lbl_phone3 || "Phone 3", "on file in ESC", false);
      pushP(c.phone4, c.lbl_phone4 || "Phone 4", "on file in ESC", false);
      // this property's lines first, then the customer's other locations
      const sortedLocs = [...((locs ?? []) as Record<string, string | null>[])].sort((a, b) => Number(b.locno === locno) - Number(a.locno === locno));
      for (const l of sortedLocs) {
        const here = l.locno === locno || !locno;
        const where = here ? "Location line" : `Location ${l.locno} line`;
        pushP(l.phone1, where, "on file in ESC", false);
        pushP(l.phone2, where, "on file in ESC", false);
        pushP(l.phone3, where, "on file in ESC", false);
        pushP(l.phone4, where, "on file in ESC", false);
        const ew = here ? "Email on file" : `Location ${l.locno} email`;
        pushE(l.email, ew, "from ESC — no preferences recorded", false);
        pushE(l.email2, ew, "from ESC — no preferences recorded", false);
        pushE(l.email3, ew, "from ESC — no preferences recorded", false);
      }
      return { phones, emails };
    },
  });
}

/* ── sending ───────────────────────────────────────────────────────────── */
export type SendResult = { ok: true } | { ok: false; gated: boolean; message: string };
const GATED_MSG = "Comms are still in testing — nothing went to the customer. Copy the link and send it from your own phone for now.";

export async function sendProposalText(v: { to: string; body: string; custno: string }): Promise<SendResult> {
  try {
    const { data, error } = await supabase.functions.invoke("podium-send", { body: { to: v.to, body: v.body, custno: v.custno } });
    const d = (data as { error?: string; blocked?: string } | null) ?? null;
    if (d?.blocked === "testing" || /testing|paused/i.test(d?.error ?? "")) return { ok: false, gated: true, message: GATED_MSG };
    if (error || d?.error) return { ok: false, gated: false, message: error?.message ?? d?.error ?? "Couldn't send — try again." };
    return { ok: true };
  } catch (e) {
    return { ok: false, gated: false, message: String(e) };
  }
}

export async function sendProposalEmail(v: { to: string[]; url: string; message: string; custno: string }): Promise<SendResult> {
  try {
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: {
        to: v.to,
        subject: PROPOSAL_EMAIL_SUBJECT,
        html: proposalEmailHtml(v.url, v.message),
        text: proposalEmailText(v.url, v.message),
        custno: v.custno,
        message_type: "proposal",
      },
    });
    const d = (data as { ok?: boolean; error?: string; blocked?: string; detail?: unknown } | null) ?? null;
    if (d?.blocked === "testing" || /testing|paused/i.test(d?.error ?? "")) return { ok: false, gated: true, message: GATED_MSG };
    if (error || d?.error || (d && d.ok === false)) return { ok: false, gated: false, message: error?.message ?? d?.error ?? "Couldn't send — try again." };
    return { ok: true };
  } catch (e) {
    return { ok: false, gated: false, message: String(e) };
  }
}

/* ── what gets recorded ────────────────────────────────────────────────── */
export type SentVia = "text" | "email" | "link";

/** Status Sent on the first send (the clock starts), sent_via/to/by every
 *  time, one specific activity line. Re-sends read "(again)". */
export async function markProposalSent(quoteId: string, via: SentVia, to?: string | null): Promise<void> {
  const { error } = await supabase.rpc("pegasus_quote_mark_sent", { p_quote_id: quoteId, p_via: via, p_to: to ?? null });
  if (error) throw error;
}

/** Staff opened the customer view: presented_at + one "Presented the customer
 *  view" line per sitting. Never touches the status. */
export async function stampProposalPresented(quoteId: string): Promise<void> {
  const { error } = await supabase.rpc("pegasus_quote_presented", { p_quote_id: quoteId });
  if (error) throw error;
}

/** Both notes, saved together (the send sheet). The office note keeps the
 *  wizard's provenance line in front of it. */
export async function saveProposalNotes(v: { quoteId: string; currentNotes: string | null | undefined; officeNote: string; homeownerNote: string }): Promise<void> {
  const { head } = splitOfficeNote(v.currentNotes);
  const { error } = await supabase
    .from("pegasus_quote")
    .update({ notes: joinOfficeNote(head, v.officeNote), homeowner_note: v.homeownerNote.trim() || null, updated_at: new Date().toISOString() })
    .eq("id", v.quoteId);
  if (error) throw error;
}

/** A mobile or email typed at send time becomes a Pegasus contact on the
 *  customer (gets job updates) — so the next send finds it. Never ESC. */
export async function addProposalContact(v: { custno: string; locno?: string | null; name: string; phone?: string | null; email?: string | null }): Promise<void> {
  const { data: u } = await supabase.auth.getUser();
  const { error } = await supabase.from("contact").insert({
    custno: v.custno,
    locno: v.locno ?? null,
    name: v.name.trim() || "Homeowner",
    phone: v.phone ? prettyPhone(v.phone) : null,
    email: v.email ? v.email.trim().toLowerCase() : null,
    source: "proposal",
    gets_scheduling: true,
    created_by: u?.user?.email ?? null,
  });
  if (error) throw error;
}

/** Copy to the clipboard; false when the browser blocks it. */
export async function copyText(s: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(s);
    return true;
  } catch {
    return false;
  }
}
